import './Concentrations.scss';
import DrugService from 'services/DrugService';
import { ReactQueryKeys } from 'constants/react-query-keys';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import React, { FC, useEffect, useState } from 'react';
import { SweetAlertResult } from 'sweetalert2';
import SwalAlert, {
  firePreConfirmAlert,
} from 'components/UI/SwalAlert/SwalAlert';
import { toast } from 'react-hot-toast';
import { ADMINISTRATION, AUTHOR } from 'constants/roles';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ListConcentration } from 'interfaces/list-concentration';
import ConcentrationList from './ConcentrationList/ConcentrationList';
import ConcentrationForm from './ConcentrationForm/ConcentrationForm';
import { IoIosAdd } from 'react-icons/io';
import { useParams } from 'react-router-dom';
import { sortConcentrations } from 'helpers/drugs';
import ReactTooltip from 'react-tooltip';
import { selectUser } from 'store/slices/auth';

/**
 * Props for the Concentrations component
 * @typedef {Object} ConcentrationProps
 * @property {number|string} drugId - The ID of the drug to display concentrations for
 */
type ConcentrationProps = {
  drugId: number | string;
};

/**
 * @description Component for displaying and managing drug concentrations in the master catalog.
 * Provides functionality to view, add, edit, and delete concentrations for a specific drug.
 *
 * @SADtrace SAD-003-3.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {ConcentrationProps} props - Component props
 *
 * @returns {JSX.Element} Rendered component
 */
const Concentrations: FC<ConcentrationProps> = ({ drugId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useSelector(selectUser);
  const params = useParams();
  // v8 ignore start -- WAIVER(MICRO): user.role is always present on an authenticated session; the '' fallback exists only to satisfy the optional user type
  const canManage = [ADMINISTRATION, AUTHOR].includes(user?.role || '');
  // v8 ignore stop -- WAIVER(MICRO)
  const [selectedConcentration, setSelectedConcentration] =
    useState<ListConcentration | null>(null);
  const [addingConcentration, setAddingConcentration] = useState(false);

  const drugsQuery = useQuery({
    queryKey: [
      ReactQueryKeys.LIST_CONCENTRATIONS,
      {
        drugId,
      },
    ],

    queryFn: () =>
      DrugService.getListConcentrations(drugId, {
        pageIndex: 0,
        pageSize: 20,
        sorting: [
          {
            id: 'name',
            desc: false,
          },
        ],
      }),

    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    setAddingConcentration(false);
  }, [params]);

  /**
   * @description Handles click on the add concentration button.
   * Opens the concentration form for adding a new concentration.
   *
   * @SADtrace SAD-003-3.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {React.MouseEvent} [event] - The click event
   */
  const handleAddClick = (event?: React.MouseEvent) => {
    event?.preventDefault();
    setAddingConcentration(true);
    setSelectedConcentration(null);
  };

  /**
   * @description Hides the add concentration form.
   *
   * @SADtrace SAD-003-3.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   */
  const hideAddConcentration = () => {
    setAddingConcentration(false);
  };

  /**
   * @description Shows a confirmation dialog for deleting a concentration.
   * If confirmed, deletes the concentration and updates the UI.
   *
   * @SADtrace SAD-003-3.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {ListConcentration} concentration - The concentration to delete
   */
  const handleDeleteShow = (concentration: ListConcentration) => {
    firePreConfirmAlert({
      title: t('concentrations.messages.modal__delete__title'),
      html: t('concentrations.messages.modal__delete__content', {
        name: concentration.name,
      }),
      preConfirm: () => {
        return DrugService.deleteListConcentration(
          String(drugId),
          String(concentration.id),
        )
          .then(({ data }) => {
            return data;
          })
          .catch((error) => {
            SwalAlert.showValidationMessage(error?.response?.data?.message);
            //return false to prevent pop up from closing when running tests, check preconfirm fn
            return false;
          });
      },
    }).then((data: SweetAlertResult) => {
      // v8 ignore start -- WAIVER(THIRDPARTY): SweetAlert2 continuation — the dismissed arm resolves inside swal's promise contract with no application behavior to assert; the confirmed path is asserted
      if (data.isConfirmed) {
        // v8 ignore stop -- WAIVER(THIRDPARTY)
        queryClient.invalidateQueries({
          queryKey: [ReactQueryKeys.LIST_CONCENTRATIONS],
        });
        queryClient.invalidateQueries({
          queryKey: [ReactQueryKeys.LIST_DRUGS],
        });
        toast.success(t('concentrations.messages.delete__success'));
      }
    });
  };

  /**
   * @description Selects a concentration for viewing/editing and hides the add form.
   *
   * @SADtrace SAD-003-3.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {ListConcentration} concentration - The concentration to select
   */
  const handleSelectConcentration = (concentration: ListConcentration) => {
    setSelectedConcentration(concentration);
    hideAddConcentration();
  };

  /**
   * @description Automatically opens the concentration form if the drug has no concentrations
   * This provides a better user experience by prompting for concentration creation
   */
  useEffect(() => {
    if (!drugsQuery.data?.data.records.length && !drugsQuery.isFetching) {
      handleAddClick();
    }
  }, [drugsQuery.data?.data.records.length, drugsQuery.isFetching]);

  return (
    <div className="row concentrations-list">
      <div className="col-5">
        <label htmlFor="careAreaName" className="form-label d-flex gap-4">
          <h5>{t('drugs.concentrations')}</h5>
          {canManage ? (
            <>
              <button
                className={`btn-rounded btn-rounded-sm concentrations_button-add`}
                disabled={addingConcentration}
                onClick={handleAddClick}
                data-tip
                data-for="concentration-add-tooltip"
                data-testid="concentration-add-button"
              >
                <IoIosAdd size={40} />
              </button>
              <ReactTooltip
                id="concentration-add-tooltip"
                effect="solid"
                place="top"
              >
                Add Concentration
              </ReactTooltip>
            </>
          ) : null}
        </label>
        <ConcentrationList
          canManage={canManage}
          onDelete={handleDeleteShow}
          onSelect={handleSelectConcentration}
          selectedConcentration={selectedConcentration}
          concentrations={sortConcentrations(
            drugsQuery.data?.data.records ?? [],
          )}
        />
      </div>
      <div className="col-7">
        {addingConcentration && (
          <ConcentrationForm hideForm={hideAddConcentration} drugId={drugId} />
        )}
      </div>
    </div>
  );
};

export default Concentrations;
